import type Phaser from "phaser";
import { CANVAS_COLOURS, type DensityGrade, type DensityRect, type ViewRect } from "daedalus-core";
import { dashesAlong } from "./camera-stop-view.ts";

/**
 * The density meter's area on the map (ADR-088 decision 7): while the Density drawer is open, the chunk range
 * measured drawn as a light tint with an outline in the colour of its worst reading (green, amber or red), the
 * view it was measured around dashed inside it in ink, and each rectangle a group reaching around the view is
 * counted over (the server's interest) dashed in the entity colour. One `Graphics` object, redrawn only when what
 * it shows changes (the range, the view, the rectangles, the grade or the zoom), never every frame; the change
 * is found by comparing those fields, with no allocation while nothing changed.
 */

/**
 * The range's tint, as a share of its colour.
 */
export const DENSITY_FILL_ALPHA = 0.08;

/**
 * The range's outline width, in screen pixels at any zoom.
 */
export const DENSITY_LINE = 2;

/**
 * A dash of the view's and the rectangles' outlines, in screen pixels at any zoom.
 */
export const DENSITY_DASH = 6;

/**
 * The gap after each dash, in screen pixels at any zoom.
 */
export const DENSITY_GAP = 4;

/**
 * What the view draws: the range's rectangle, the view inside it, the rectangles groups reaching around the view
 * are counted over, and the worst grade.
 */
export interface DensityShape {
  readonly rect: DensityRect;
  readonly view: ViewRect;
  readonly reaches: readonly DensityRect[];
  readonly grade: DensityGrade | null;
}

/**
 * The canvas colour of a grade, green when none is graded.
 *
 * @param grade The grade.
 * @returns The colour.
 */
export function densityColour(grade: DensityGrade | null): { readonly rgb: number; readonly alpha: number } {
  return grade === "red" ? CANVAS_COLOURS.densityRed : grade === "amber" ? CANVAS_COLOURS.densityAmber : CANVAS_COLOURS.densityGreen;
}

/**
 * Whether two rectangles are equal.
 *
 * @param a One.
 * @param b The other.
 * @returns True when every field matches.
 */
function sameRect(a: DensityRect, b: DensityRect): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

/**
 * Whether two shapes draw the same.
 *
 * @param a One.
 * @param b The other.
 * @returns True when the range, the view, the rectangles and the grade match.
 */
function sameShape(a: DensityShape, b: DensityShape): boolean {
  if (a.grade !== b.grade || !sameRect(a.rect, b.rect) || a.reaches.length !== b.reaches.length) {
    return false;
  }
  const v = a.view;
  const w = b.view;
  if (v.left !== w.left || v.top !== w.top || v.width !== w.width || v.height !== w.height) {
    return false;
  }
  for (let i = 0; i < a.reaches.length; i += 1) {
    const one = a.reaches[i];
    const other = b.reaches[i];
    if (one === undefined || other === undefined || !sameRect(one, other)) {
      return false;
    }
  }
  return true;
}

/**
 * The meter's area in one scene.
 */
export class DensityRangeView {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private drawn: DensityShape | null = null;
  private drawnZoom = 0;
  private started = false;
  private draws = 0;

  /**
   * @param scene Scene to draw into.
   * @param depth Depth over the map's layers and under the editor's markers.
   */
  public constructor(scene: Phaser.Scene, depth: number) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * How many times the area was drawn, which only a change of what it shows does.
   *
   * @returns The count.
   */
  public get redraws(): number {
    return this.draws;
  }

  /**
   * Draws the area, or clears it, when anything it shows changed since the last call.
   *
   * @param shape What to draw, or null to show nothing.
   * @param zoom The camera's zoom, which line widths and dashes are divided by.
   * @returns True when a redraw happened.
   */
  public sync(shape: DensityShape | null, zoom: number): boolean {
    const was = this.drawn;
    if (this.started && (shape === null ? was === null : was !== null && zoom === this.drawnZoom && sameShape(was, shape))) {
      return false;
    }
    this.started = true;
    this.drawn = shape;
    this.drawnZoom = zoom;
    this.draws += 1;
    const g = this.graphics;
    g.clear();
    if (shape === null) {
      return true;
    }
    const { rect, view } = shape;
    const colour = densityColour(shape.grade);
    g.fillStyle(colour.rgb, colour.alpha * DENSITY_FILL_ALPHA);
    g.fillRect(rect.x, rect.y, rect.width, rect.height);
    g.lineStyle(DENSITY_LINE / zoom, colour.rgb, colour.alpha);
    g.strokeRect(rect.x, rect.y, rect.width, rect.height);
    g.lineStyle(1 / zoom, CANVAS_COLOURS.densityView.rgb, CANVAS_COLOURS.densityView.alpha);
    this.dashRect({ x: view.left, y: view.top, width: view.width, height: view.height }, zoom);
    g.lineStyle(1 / zoom, CANVAS_COLOURS.densityReach.rgb, CANVAS_COLOURS.densityReach.alpha);
    for (const reach of shape.reaches) {
      this.dashRect(reach, zoom);
    }
    return true;
  }

  /**
   * Removes the area from the scene.
   */
  public destroy(): void {
    this.graphics.destroy();
  }

  /**
   * Dashes a rectangle's four sides in the line style set.
   *
   * @param rect The rectangle in world pixels.
   * @param zoom The camera's zoom.
   */
  private dashRect(rect: DensityRect, zoom: number): void {
    const g = this.graphics;
    const dash = DENSITY_DASH / zoom;
    const gap = DENSITY_GAP / zoom;
    const right = rect.x + rect.width;
    const bottom = rect.y + rect.height;
    for (const y of [rect.y, bottom]) {
      for (const [a, b] of dashesAlong(rect.x, right, rect.x, right, dash, gap)) {
        g.lineBetween(a, y, b, y);
      }
    }
    for (const x of [rect.x, right]) {
      for (const [a, b] of dashesAlong(rect.y, bottom, rect.y, bottom, dash, gap)) {
        g.lineBetween(x, a, x, b);
      }
    }
  }
}
